import React, { useState } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * 전자 계약서 관리 모달 컴포넌트
 */
export default function ContractModal({ open, onClose, contractData }) {
  const [showContract, setShowContract] = useState(false);

  if (!open) return null;

  const handleLinkClick = () => {
    // iframe으로 계약서 보기
    setShowContract(true);
  };

  const handleOpenNewTab = () => {
    // 새 탭으로 계약서 열기
    window.open('https://app.modusign.co.kr/magic-link/link/71a3eca0-f66a-11f0-926a-23cbde5e0639/authentication?shareType=URL', '_blank');
  };

  const handleDownload = async () => {
    const downloadFilename = '골프예약 대행계약서_(주)에이치비엠피.docx';
    const url = `${process.env.PUBLIC_URL || ''}/documents/golf-booking-contract.docx`;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('NOT_FOUND');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleComplete = () => {
    // 계약 완료 로직
    console.log('계약 완료');
    onClose();
  };

  const handleBack = () => {
    setShowContract(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className={`relative w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
        showContract ? 'max-w-[80vw] h-[80vh]' : 'max-w-2xl max-h-[90vh]'
      }`}>
        {showContract ? (
          /* 계약서 보기 화면 */
          <>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-xl">←</span>
                </button>
                <h3 className="text-xl font-bold text-black">
                  골프대행 계약서
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none p-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* iframe Container */}
            <div className="flex-1 overflow-hidden min-h-0">
              <iframe
                style={{ border: '1px solid rgba(0, 0, 0, 0.1)' }}
                width="100%"
                height="100%"
                src="https://app.modusign.co.kr/magic-link/link/f2bfaed0-f68f-11f0-94c6-1f0807249ecf/authentication?shareType=EMBED_CODE"
                allowFullScreen
                title="골프대행 계약서"
              />
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={handleBack}
                className="px-6 h-12 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold rounded-lg transition-colors"
              >
                뒤로가기
              </button>
            </div>
          </>
        ) : (
          /* 정보 화면 */
          <>
            {/* Header - 상단 오른쪽 X 버튼만 */}
            <div className="relative p-8 pb-6">
              <button
                onClick={onClose}
                className="absolute top-6 right-8 text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none p-2"
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="text-2xl md:text-3xl font-bold text-black mb-2 leading-tight">
                골프대행 계약서
              </h3>
              <p className="text-base font-bold text-black mt-3">계약서를 서명하거나 확인하려면 아래 방법 중 하나를 선택해 주세요.
              </p>
            </div>

        {/* Form Body - 2컬럼 레이아웃 */}
        <div className="px-8 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6">
            {/* 왼쪽: 빠른 접근 방식 */}
            <div className="space-y-4">
              <div className="space-y-3">
                <button 
                  onClick={handleLinkClick}
                  className="w-full flex items-center justify-between p-4 bg-white border-2 border-green-500 text-green-700 rounded-lg hover:bg-green-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <DescriptionIcon sx={{ fontSize: 28, color: '#15803d' }} />
                    <div className="text-left">
                      <p className="text-sm font-bold">전자계약서 서명하기</p>
                      <p className="text-xs text-gray-500">PC에서 서명 및 확인</p>
                    </div>
                  </div>
                </button>

                {/* 다운로드 버튼 */}
                <button 
                  onClick={handleDownload}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <DownloadIcon sx={{ fontSize: 28, color: '#374151' }} />
                    <div className="text-left">
                      <p className="text-sm font-bold">계약서 다운로드</p>
                      <p className="text-xs text-gray-500">파일로 저장</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* 오른쪽: QR 코드 영역 */}
            <div className="flex flex-col items-center justify-center bg-green-50 rounded-xl p-6 border border-green-100">
              <div className="text-center mb-4">
                <h4 className="text-base font-bold text-gray-800">모바일 서명 / QR 확인</h4>
                <p className="text-xs text-gray-600 mt-1">카메라로 QR 코드를 스캔해 주세요.</p>
              </div>
              
              <div className="bg-white p-3 rounded-lg shadow-md">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/QRCode.png`}
                  alt="골프대행 계약서 QR 코드"
                  className="w-24 h-24 object-contain"
                />
              </div>

            </div>
          </div>

          {/* 하단 안내 */}
          <p className="text-sm text-gray-500 text-center pt-6 border-t border-gray-100 mt-6">
          티샷은 전자계약 서비스 모두싸인을 통해 안전하게 계약을 체결합니다.
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
